import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import adminService from '../../utils/adminService';
import '../../styles/ServiceForm.css';

const ServiceForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    slug: '',
    name_pt: '',
    name_en: '',
    description_pt: '',
    description_en: '',
    price: '',
    duration: '60',
    icon: '🔮',
    category: 'tarot',
    bookingEnabled: true,
    isActive: true,
    order: '0'
  });

  const [features, setFeatures] = useState([
    { pt: '', en: '' }
  ]);

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (isEdit) {
      loadService();
    }
  }, [id]);

  const loadService = async () => {
    const result = await adminService.getService(id);
    
    if (result.success) {
      const service = result.service;
      setFormData({
        slug: service.slug,
        name_pt: service.name.pt,
        name_en: service.name.en,
        description_pt: service.description.pt,
        description_en: service.description.en,
        price: service.price.toString(),
        duration: service.duration.toString(),
        icon: service.icon,
        category: service.category,
        bookingEnabled: service.bookingEnabled,
        isActive: service.isActive,
        order: service.order.toString()
      });

      if (service.features && service.features.length > 0) {
        setFeatures(service.features);
      }

      if (service.image) {
        setImagePreview(service.image);
      }
    } else {
      setMessage({ type: 'error', text: result.message });
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFeatureChange = (index, field, value) => {
    const newFeatures = [...features];
    newFeatures[index][field] = value;
    setFeatures(newFeatures);
  };

  const addFeature = () => {
    setFeatures([...features, { pt: '', en: '' }]);
  };

  const removeFeature = (index) => {
    const newFeatures = features.filter((_, i) => i !== index);
    setFeatures(newFeatures);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    // Create FormData
    const data = new FormData();
    Object.keys(formData).forEach(key => {
      data.append(key, formData[key]);
    });

    // Add features
    const featuresPt = features.map(f => f.pt).filter(f => f.trim());
    const featuresEn = features.map(f => f.en).filter(f => f.trim());
    data.append('features_pt', JSON.stringify(featuresPt));
    data.append('features_en', JSON.stringify(featuresEn));

    // Add image if selected
    if (imageFile) {
      data.append('image', imageFile);
    }

    // Submit
    const result = isEdit 
      ? await adminService.updateService(id, data)
      : await adminService.createService(data);

    if (result.success) {
      setMessage({ 
        type: 'success', 
        text: isEdit ? 'Service updated successfully!' : 'Service created successfully!' 
      });
      setTimeout(() => navigate('/admin/services'), 1500);
    } else {
      setMessage({ type: 'error', text: result.message });
    }

    setLoading(false);
  };

  return (
    <div className="service-form-page">
      <div className="admin-header">
        <h1>{isEdit ? 'Edit Service' : 'Create New Service'}</h1>
        <button onClick={() => navigate('/admin/services')} className="btn btn-secondary">
          ← Back to Services
        </button>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type}`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="service-form">
        <div className="form-section">
          <h3>Basic Information</h3>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="slug">Slug (URL identifier) *</label>
              <input
                type="text"
                id="slug"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                placeholder="tarot-reading"
                required
              />
              <small>Used in URLs, lowercase letters and hyphens only</small>
            </div>

            <div className="form-group">
              <label htmlFor="icon">Icon</label>
              <input
                type="text"
                id="icon"
                name="icon"
                value={formData.icon}
                onChange={handleChange}
                placeholder="🔮"
              />
              <small>Emoji or icon character</small>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="category">Category *</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
              >
                <option value="tarot">Tarot</option>
                <option value="astrology">Astrology</option>
                <option value="crystals">Crystals</option>
                <option value="healing">Healing</option>
                <option value="coaching">Coaching</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="order">Display Order</label>
              <input
                type="number"
                id="order"
                name="order"
                value={formData.order}
                onChange={handleChange}
                min="0"
              />
              <small>Lower numbers appear first</small>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Names (Multilingual)</h3>

          <div className="form-group">
            <label htmlFor="name_pt">Name (Portuguese) *</label>
            <input
              type="text"
              id="name_pt"
              name="name_pt"
              value={formData.name_pt}
              onChange={handleChange}
              placeholder="Leitura de Tarot"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="name_en">Name (English) *</label>
            <input
              type="text"
              id="name_en"
              name="name_en"
              value={formData.name_en}
              onChange={handleChange}
              placeholder="Tarot Reading"
              required
            />
          </div>
        </div>

        <div className="form-section">
          <h3>Descriptions (Multilingual)</h3>

          <div className="form-group">
            <label htmlFor="description_pt">Description (Portuguese) *</label>
            <textarea
              id="description_pt"
              name="description_pt"
              value={formData.description_pt}
              onChange={handleChange}
              rows="4"
              placeholder="Descrição completa do serviço..."
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description_en">Description (English) *</label>
            <textarea
              id="description_en"
              name="description_en"
              value={formData.description_en}
              onChange={handleChange}
              rows="4"
              placeholder="Complete service description..."
              required
            />
          </div>
        </div>

        <div className="form-section">
          <h3>Pricing & Duration</h3>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="price">Price (EUR) *</label>
              <input
                type="number"
                id="price"
                name="price"
                value={formData.price}
                onChange={handleChange}
                min="0"
                step="0.01"
                placeholder="40.00"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="duration">Duration (minutes) *</label>
              <input
                type="number"
                id="duration"
                name="duration"
                value={formData.duration}
                onChange={handleChange}
                min="15"
                step="15"
                placeholder="60"
                required
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Features (Multilingual)</h3>
          <p className="section-description">Add the key features or benefits of this service</p>

          {features.map((feature, index) => (
            <div key={index} className="feature-row">
              <div className="feature-inputs">
                <input
                  type="text"
                  value={feature.pt}
                  onChange={(e) => handleFeatureChange(index, 'pt', e.target.value)}
                  placeholder="Feature in Portuguese"
                />
                <input
                  type="text"
                  value={feature.en}
                  onChange={(e) => handleFeatureChange(index, 'en', e.target.value)}
                  placeholder="Feature in English"
                />
              </div>
              {features.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeFeature(index)}
                  className="btn-remove-feature"
                >
                  ✕
                </button>
              )}
            </div>
          ))}

          <button type="button" onClick={addFeature} className="btn btn-secondary btn-sm">
            ➕ Add Feature
          </button>
        </div>

        <div className="form-section">
          <h3>Image</h3>

          {imagePreview && (
            <div className="image-preview">
              <img src={imagePreview} alt="Service preview" />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="image">Upload Image</label>
            <input
              type="file"
              id="image"
              accept="image/*"
              onChange={handleImageChange}
            />
            <small>Recommended: 600x400px, max 5MB (jpg, png, gif, webp)</small>
          </div>
        </div>

        <div className="form-section">
          <h3>Settings</h3>

          <div className="form-checkboxes">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="bookingEnabled"
                checked={formData.bookingEnabled}
                onChange={handleChange}
              />
              <span>Enable booking for this service</span>
            </label>

            <label className="checkbox-label">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
              />
              <span>Service is active (visible on website)</span>
            </label>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/admin/services')}
            className="btn btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Saving...' : isEdit ? 'Update Service' : 'Create Service'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ServiceForm;
